import { useTranslation } from '@hooks/useTranslation';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { graphQlClient } from '@config/graphqlClient';
import ProcessExecutionGraphQL from '@graphql/processExecution.queries';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import WolfButton from '@components/ui/WolfButton';
import { useSelector } from 'react-redux';
import { RootState } from '@redux/store';

interface SkipTriggerExecutionResponse {
  skipTriggerExecution: {
    success: boolean;
    message: string;
  };
}

const SkipPage: React.FC = () => {
  const localeProcesses = useTranslation('processes');
  const localeCommon = useTranslation('common');
  const query = useLocationQuery();
  const history = useHistory();
  const triggerId = query.get('triggerId');
  const user = useSelector((state: RootState) => state.user);

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<boolean | null>(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check if user is authenticated
    if (!user?.userId) {
      // Not logged in - redirect to login and return here after
      const currentUrl = window.location.pathname + window.location.search;
      window.location.href = `/login?redirect=${encodeURIComponent(currentUrl)}`;
      return;
    }

    if (!triggerId) {
      setSuccess(false);
      setMessage(localeProcesses['missingTriggerId']);
      setLoading(false);
      return;
    }

    // Execute the skip mutation
    const skipExecution = async () => {
      try {
        const response: SkipTriggerExecutionResponse = await graphQlClient.request(
          ProcessExecutionGraphQL.mutations.skipTriggerExecution,
          { triggerId }
        );

        if (response.skipTriggerExecution.success) {
          setSuccess(true);
          setMessage(response.skipTriggerExecution.message || localeProcesses['executionSkippedSuccess']);
        } else {
          setSuccess(false);
          setMessage(response.skipTriggerExecution.message || localeProcesses['executionSkippedError']);
        }
      } catch (error: any) {
        console.error('Error skipping execution:', error);
        setSuccess(false);
        setMessage(error?.response?.errors?.[0]?.message || localeCommon['requestError']);
      } finally {
        setLoading(false);
      }
    };

    skipExecution();
  }, [triggerId, user, localeProcesses, localeCommon]);

  const handleGoToDashboard = () => {
    history.push('/processes');
  };

  const handleGoToExecutions = () => {
    history.push('/executions');
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center min-h-screen">
        <Paper className="p-8 max-w-md w-full text-center">
          <CircularProgress size={48} className="mb-4" />
          <Typography variant="h6" className="mb-2">
            {localeProcesses['processingSkip']}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {localeProcesses['pleaseWait']}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className="flex items-center justify-center min-h-screen p-4">
      <Paper className="p-8 max-w-md w-full">
        <Box className="flex flex-col items-center text-center">
          {success ? (
            <>
              <CheckCircleOutline
                sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
              />
              <Typography variant="h5" className="mb-2">
                {localeProcesses['executionSkipped']}
              </Typography>
              <Typography variant="body1" color="text.secondary" className="mb-4">
                {message}
              </Typography>
            </>
          ) : (
            <>
              <ErrorOutline
                sx={{ fontSize: 64, color: 'error.main', mb: 2 }}
              />
              <Typography variant="h5" className="mb-2">
                {localeProcesses['skipFailed']}
              </Typography>
              <Typography variant="body1" color="text.secondary" className="mb-4">
                {message}
              </Typography>
            </>
          )}

          <Box className="flex gap-2 mt-4">
            <WolfButton color="tertiary" onClick={handleGoToDashboard}>
              {localeCommon['goToDashboard']}
            </WolfButton>
            <WolfButton color="primary" onClick={handleGoToExecutions}>
              {localeProcesses['viewExecutions']}
            </WolfButton>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SkipPage;
